import { Controller } from "@hotwired/stimulus"

/**
 * Invoice lines controller for the manual review form.
 *
 * Connects to data-controller="invoice-lines"
 *
 * Responsibilities:
 *   - Toggle between the itemized line list and the non-itemized summary
 *     based on the line_itemization radio choice. Hidden panels are disabled
 *     so their fields are neither submitted nor focusable.
 *   - Add lines from a template (NEW_RECORD placeholder), remove lines
 *     (soft-destroy for persisted lines), and reorder lines with explicit
 *     move up/down buttons so reordering works without drag and drop.
 *   - Keep the hidden position inputs, visible line numbers, and button
 *     labels in sync so positions persist through a normal form submit.
 *
 * Targets:
 *   - itemizedPanel / summaryPanel: the two mutually exclusive panels
 *   - list: container receiving new line elements
 *   - template: template with NEW_RECORD placeholders
 *   - line: one line element inside the list
 *   - announcer: aria-live region for line changes
 */
export default class extends Controller {
  static targets = ["itemizedPanel", "summaryPanel", "list", "template", "line", "announcer"]
  static values = {
    addedMessage: { type: String, default: "Line %{position} added." },
    removedMessage: { type: String, default: "Line removed. %{count} lines remain." },
    movedMessage: { type: String, default: "Line moved to position %{position}." },
    moveUpLabel: { type: String, default: "Move line %{position} up" },
    moveDownLabel: { type: String, default: "Move line %{position} down" },
    removeLabel: { type: String, default: "Remove line %{position}" }
  }

  connect() {
    this.newRecordSequence = 0
    this.toggleMode()
    this.renumber()
  }

  toggleMode() {
    const selected = this.element.querySelector('input[name*="[line_itemization]"]:checked')?.value
    this.setPanelState(this.itemizedPanelTarget, selected !== "non_itemized")
    this.setPanelState(this.summaryPanelTarget, selected === "non_itemized")
    this.renumber()
  }

  add(event) {
    event.preventDefault()
    this.newRecordSequence += 1
    const key = `${new Date().getTime()}${this.newRecordSequence}`
    const content = this.templateTarget.innerHTML.replaceAll("NEW_RECORD", key)
    this.listTarget.insertAdjacentHTML("beforeend", content)
    this.renumber()

    const line = this.listTarget.lastElementChild
    this.announce(this.interpolate(this.addedMessageValue, { position: this.visibleLines().length }))
    line?.querySelector("input:not([type=hidden]), select, textarea")?.focus()
  }

  removeLine(event) {
    event.preventDefault()
    const line = event.target.closest("[data-invoice-lines-target~='line']")
    if (!line) return

    const idInput = line.querySelector("input[name*='[id]']")
    if (idInput && idInput.value) {
      const destroyInput = line.querySelector("input[name*='[_destroy]']")
      if (destroyInput) destroyInput.value = "1"
      line.hidden = true
      line.querySelectorAll("input, select, textarea, button").forEach((control) => {
        if (control !== destroyInput && control !== idInput) control.disabled = true
      })
    } else {
      line.remove()
    }

    this.renumber()
    this.announce(this.interpolate(this.removedMessageValue, { count: this.visibleLines().length }))
  }

  moveUp(event) {
    event.preventDefault()
    this.move(event.target.closest("[data-invoice-lines-target~='line']"), -1)
  }

  moveDown(event) {
    event.preventDefault()
    this.move(event.target.closest("[data-invoice-lines-target~='line']"), 1)
  }

  move(line, direction) {
    if (!line) return

    const lines = this.visibleLines()
    const index = lines.indexOf(line)
    const swapWith = lines[index + direction]
    if (!swapWith) return

    if (direction < 0) {
      this.listTarget.insertBefore(line, swapWith)
    } else {
      this.listTarget.insertBefore(swapWith, line)
    }

    this.renumber()
    this.announce(this.interpolate(this.movedMessageValue, { position: index + direction + 1 }))
  }

  setPanelState(panel, enabled) {
    panel.hidden = !enabled
    panel.querySelectorAll("input, select, textarea, button").forEach((control) => {
      control.disabled = !enabled
    })
  }

  visibleLines() {
    return Array
      .from(this.listTarget.querySelectorAll("[data-invoice-lines-target~='line']"))
      .filter((line) => !line.hidden)
  }

  renumber() {
    const lines = this.visibleLines()
    lines.forEach((line, index) => {
      const position = index + 1
      const positionInput = line.querySelector("input[name*='[position]']")
      if (positionInput) positionInput.value = position

      line.querySelectorAll("[data-invoice-lines-line-number]").forEach((numberElement) => {
        numberElement.textContent = position
      })

      this.setButtonLabel(line, "moveUp", this.interpolate(this.moveUpLabelValue, { position }))
      this.setButtonLabel(line, "moveDown", this.interpolate(this.moveDownLabelValue, { position }))
      this.setButtonLabel(line, "removeLine", this.interpolate(this.removeLabelValue, { position }))
      this.setMoveButtonState(line, "moveUp", index === 0)
      this.setMoveButtonState(line, "moveDown", index === lines.length - 1)
    })
  }

  setMoveButtonState(line, action, disabled) {
    const button = line.querySelector(`button[data-action~='invoice-lines#${action}']`)
    if (!button) return

    button.disabled = disabled
    button.setAttribute("aria-disabled", disabled.toString())
  }

  setButtonLabel(line, action, label) {
    const button = line.querySelector(`button[data-action~='invoice-lines#${action}']`)
    if (!button) return

    button.setAttribute("aria-label", label)
    const text = button.querySelector("[data-invoice-lines-button-text]")
    if (text) text.textContent = label
  }

  announce(message) {
    if (!this.hasAnnouncerTarget) return

    this.announcerTarget.textContent = ""
    requestAnimationFrame(() => {
      this.announcerTarget.textContent = message
    })
  }

  interpolate(template, values) {
    return Object.entries(values).reduce(
      (message, [key, value]) => message.replaceAll(`%{${key}}`, value),
      template
    )
  }
}
